import { useState, useMemo } from 'react';
import { Formik, Field } from 'formik';
import * as Yup from 'yup';
import i18n from 'i18next';

import { Flex } from 'web-styled';
import {
  Modal,
  Button,
  Tooltip,
  HelpText,
  TextField,
  ThreeFolders,
  SelectField,
} from 'web-components';

type ResourceType = 'workoutTemplates' | 'programTemplates';

type FormValues = {
  name: string;
  client_id?: string;
  folder_id?: number;
};

type Props = {
  clients: any[];
  isOpen: boolean;
  folderName?: string;
  closeButton?: boolean;
  buttonTooltip?: string;
  helpText?: string;
  resourceType?: ResourceType;
  onSubmit: (data: FormValues, actions: any) => void;
  onRequestClose: () => void;
};

const validationSchema = Yup.object().shape({
  name: Yup.string().required('Name is required'),
});

const INITIAL_VALUES: FormValues = {
  name: '',
  client_id: '',
}

const AddTemplateModal = ({
  isOpen,
  clients,
  folderName,
  closeButton,
  buttonTooltip,
  resourceType,
  onSubmit,
  onRequestClose,
}: Props) => {
  const [selectClientVisible, setClientSelectVisible] = useState(false);

  const showClientSelect = () => setClientSelectVisible(true);
  const hideClientSelect = () => setClientSelectVisible(false);

  const clientLabel = useMemo(() => {
    const t = i18n.t('client')
    return t.charAt(0).toUpperCase() + t.slice(1)
  }, [])

  const renderClientSelectDropdown = () => (
    <Flex width="250px" alignSelf="flex-end">
      <Field
        name="client_id"
        autoFocus
        options={clients}
        component={SelectField}
        menuIsOpen
        placeholder={`Search ${i18n.t('client_plural')}`}
        maxMenuHeight={300}
        theme={{ borderRadius: 5 }}
        onBlur={hideClientSelect}
        onChange={hideClientSelect}
      />
    </Flex>
  )

  const renderActionButtons = (formikProps: any) => {
    const hasClientSelected = !!formikProps.values.client_id;

    return (
      <Flex width="100%" justify="flex-end" spacing={20}>
        {closeButton && (
          <Button
            ghost
            appearance="danger"
            spacing="large"
            onClick={onRequestClose}
          >
            Close
          </Button>
        )}

        {hasClientSelected && (
          <Button
            ghost
            spacing="large"
            appearance="danger"
            onClick={() => formikProps.setFieldValue('client_id', '')}
            isDisabled={formikProps.isSubmitting}
          >
            Cancel
          </Button>
        )}

        <Button
          ghost
          type={hasClientSelected ? 'submit' : 'button'}
          spacing="large"
          appearance="primary"
          onClick={() => {
            if (!hasClientSelected) {
              showClientSelect();
            }
          }}
          isLoading={hasClientSelected ? formikProps.isSubmitting : undefined}
          isDisabled={hasClientSelected ? formikProps.isSubmitting : undefined}
        >
          {`Create for ${clientLabel}`}
        </Button>

        {!hasClientSelected && renderSubmitButton(formikProps)}
      </Flex>
    );
  }

  const renderSubmitButton = (formikProps: any) => {
    const button = (
      <Button
        type="submit"
        spacing="large"
        appearance="primary"
        isLoading={formikProps.isSubmitting}
        isDisabled={formikProps.isSubmitting}
      >
        Add as Template
      </Button>
    );

    if (buttonTooltip) {
      return (
        <Tooltip
          body={buttonTooltip}
          positions={['left']}
          toggleMode
          initialState
        >
          {button}
        </Tooltip>
      );
    }

    return button;
  };

  return (
    <Modal
      title={i18n.t(`add.${resourceType}`)}
      isOpen={isOpen}
      onRequestClose={onRequestClose}
    >
      <Formik
        initialValues={INITIAL_VALUES}
        onSubmit={onSubmit}
        validateOnBlur={false}
        validateOnChange={false}
        validationSchema={validationSchema}
      >
        {(formikProps) => (
          <Flex
            column
            width="100%"
            as="form"
            spacing={15}
            onSubmit={formikProps.handleSubmit}
          >
            <Field
              autoFocus
              name="name"
              label={i18n.t(`name.label.${resourceType}`)}
              placeholder={i18n.t(`name.placeholder.${resourceType}`)}
              component={TextField}
            />

            {formikProps.values.client_id && (
              <Field
                name="client_id"
                label={clientLabel}
                options={clients}
                component={SelectField}
                maxMenuHeight={300}
                theme={{ borderRadius: 5 }}
              />
            )}

            {(folderName && !formikProps.values.client_id) && (
              <ThreeFolders
                selectedId={formikProps.values.folder_id}
                folderName={folderName}
                onSelect={(id) => formikProps.setFieldValue('folder_id', id)}
              />
            )}

            {selectClientVisible
              ? renderClientSelectDropdown()
              : renderActionButtons(formikProps)}

            <hr style={{ marginTop: 10 }} />
            <HelpText html={i18n.t(`add.${resourceType}.trainer.help`)} />
          </Flex>
        )}
      </Formik>
    </Modal>
  );
};

export default AddTemplateModal;
